/* miscTargets.js -- WHICH INCOME-STATEMENT LINE A MISC REVENUE LINE MAY ROUTE ONTO (W-009).
 *
 * Brian, 2026-09-23: a misc line may RESUME ANY UNCLAIMED HISTORICAL REVENUE LINE -- e.g. a line
 * the tenant chose not to forecast at onboarding and wants to forecast later. History and
 * forecast then sit on ONE continuous row (the Professional Development pattern). This replaces
 * the picker's old source, the revenue_module_mappings rows with module_type='misc_revenue',
 * which made that case unreachable on screen.
 *
 * THE SET, for the line being edited:
 *   (1) a leaf the BUILT income statement actually shows under total_revenue (the skeleton's
 *       total_revenue.sumOf -- the same source the implementation picker reads), that is
 *   (2) a REVENUE line of this company: a gl_line_mappings row with statement_type 'IS' and
 *       handled_by 'revenue_module' (the operator-set positive declaration; never a name test),
 *       OR a revenue_module_mappings row with module_type 'misc_revenue' for the key (2026-11-02),
 *   (3) MINUS every leaf another revenue module claims: the AE Quota and Company Quota targets
 *       (revenue_module_mappings.module_type -- how the engine's getModuleTargetLine finds them),
 *       every implementation line's target_gl_line_key, and every OTHER misc line's target.
 *
 * THE SERVER HOLDS THE SAME RULE (server.js validateMiscTarget). THE WALL means the two are
 * computed separately -- the server cannot run buildISLines, so it mirrors the builder's
 * emission rule in SQL -- and the W-009 build verify asserts they accept the same keys on every
 * tenant. The client set is the courtesy; the server guard is the guarantee.
 *
 * LABELS come from the line's OWNER, gl_line_mappings.display_name (leafLabels.ownedLabelFor,
 * the 09-22 vendor-label precedent) -- never a raw key and never the rmm cache. */
import { indexGlByLeafKey, ownedLabelFor } from './leafLabels';
import { leafKeyFor } from './leafKey';

/* The module types whose revenue_module_mappings row CLAIMS its gl_line_key. A claim is by
 * mapping, whether or not the module is enabled today: enabling it later would overwrite. */
export const CLAIMING_MODULE_TYPES = ['ae_quota', 'company_quota'];

/* W-016 -- THE ONE CLIENT CLAIMANT DERIVATION, the mirror of the server's revenueTargetClaims (THE WALL: computed separately,
 * asserted equal in the build verify). For every revenue line: which OTHER streams already aim at it -- the AE / CQ mapping
 * (revenue_module_mappings by module_type), every implementation line's target, every misc line's target -- leaving out the row
 * being edited (implLineId / miscLineId). A misc_revenue MAPPING row is NOT a claimant: the Path-A pair (a misc line on its own
 * mapping's line) is ONE stream. Read by the misc picker below, the implementation picker (RevenueBuildPage) and the Step-7
 * AE / CQ picker (AdminPage). Returns Map<key, [{ kind, id }]>. */
export function revenueTargetClaimants({ revMappings, implementationLines, miscLines, miscLineId, implLineId }) {
  const out = new Map();
  const add = (k, c) => { if (!k) return; if (!out.has(k)) out.set(k, []); out.get(k).push(c); };
  for (const r of revMappings || []) {
    if (r && CLAIMING_MODULE_TYPES.includes(r.module_type)) add(r.gl_line_key, { kind: r.module_type });
  }
  for (const i of implementationLines || []) {
    if (i && String(i.id) !== String(implLineId)) add(i.target_gl_line_key, { kind: 'implementation', id: i.id });
  }
  for (const m of miscLines || []) {
    if (m && String(m.id) !== String(miscLineId)) add(m.target_gl_line_key, { kind: 'misc', id: m.id });
  }
  return out;
}

export function claimedRevenueTargets(args) {
  return new Set(revenueTargetClaimants(args).keys());
}

export function miscTargetOptions({ isLines, glMappings, revMappings, implementationLines, miscLines, lineId }) {
  const total = (isLines || []).find(l => l && l.id === 'total_revenue');
  const emitted = Array.isArray(total && total.sumOf) ? total.sumOf : [];
  const revenueKeys = new Set();
  for (const g of glMappings || []) {
    if (g && g.statement_type === 'IS' && g.handled_by === 'revenue_module') {
      const k = leafKeyFor(g);
      if (k) revenueKeys.add(k);
    }
  }
  /* 2026-11-02 (PantryProof): the OTHER stored declaration that a line is revenue -- a revenue_module_mappings row with
   * module_type 'misc_revenue' (the line's own Step-7 Misc Revenue mapping). No screen writes handled_by 'revenue_module'
   * for a company onboarded through the wizard, so without this arm the picker offered nothing there and "Route to
   * existing IS line" was disabled. ONLY misc_revenue rows join: a skip / ae_quota / company_quota / implementation row
   * does not. The emitted / claimed filters below are unchanged. Mirrored on the server (validateMiscTarget). */
  for (const r of revMappings || []) {
    if (r && r.module_type === 'misc_revenue' && r.gl_line_key) revenueKeys.add(r.gl_line_key);
  }
  // W-016: the ONE claimant derivation above (the misc line being edited left out; every implementation line counts)
  const claimed = claimedRevenueTargets({ revMappings, implementationLines, miscLines, miscLineId: lineId });
  const byKey = indexGlByLeafKey(glMappings);
  const leafLabel = new Map((isLines || []).map(l => [l && l.id, l && l.label]));
  const seen = new Set();
  const out = [];
  for (const k of emitted) {
    if (seen.has(k) || !revenueKeys.has(k) || claimed.has(k)) continue;
    seen.add(k);
    out.push({ key: k, label: ownedLabelFor(byKey, k) || leafLabel.get(k) || k });
  }
  return out;
}

/* The owner's label for a routed key, for the "-> <line>" caption and a stored target the
 * option list no longer offers. Falls back to the key only when no gl_line_mappings row owns
 * it (not a state any stored row is in today). */
export function miscTargetLabel(glMappings, key) {
  if (!key) return '';
  return ownedLabelFor(indexGlByLeafKey(glMappings), key) || key;
}
